'use client';

import React from 'react';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Link from 'next/link';
import { z } from 'zod';

import { GetCoursesByQuery } from '@/app/api/courses/query/route';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/react-hook-form/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useCommandMenu } from '@/context/command-menu-provider';
import { CourseQuery } from '@/lib/validations/course';

const FormSchema = z.object({
  course: z
    .string({ required_error: 'Please enter your search terms' })
    .min(2, {
      message: 'Course name/code must be at least 2 characters.'
    })
});

export function SearchCourseDialog() {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<GetCoursesByQuery>([]);
  const { isSearching, setIsSearching } = useCommandMenu();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      course: ''
    }
  });

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        setIsSearching((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    setLoading(true);
    try {
      const response = await fetch('/api/courses/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: data.course
        } satisfies CourseQuery),
        cache: 'no-cache'
      });

      if (!response.ok) {
        setData([]);
        return;
      }

      const courses = await response.json();
      setData(courses);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={isSearching}
      onOpenChange={(open) => setIsSearching(open)}
      modal
    >
      <DialogContent className="overflow-hidden p-0 shadow-2xl">
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Search</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 p-4"
              id="course-search"
            >
              <FormField
                control={form.control}
                name="course"
                render={({ field }) => (
                  <FormItem className="px-1">
                    <FormControl>
                      <input
                        placeholder="Search courses..."
                        {...field}
                        className="w-[95%] bg-transparent outline-none placeholder:text-muted-foreground focus:outline-none"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <Separator />
          <div className="h-full max-h-80 overflow-y-auto px-2 py-4">
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : data.length <= 0 ? (
              <p className="text-center text-muted-foreground">
                No results found.
              </p>
            ) : (
              data.map((course) => (
                <Button
                  variant="ghost"
                  className="inline-flex w-full items-center justify-start"
                  key={course.id}
                  asChild
                >
                  <Link
                    href={
                      course.type === 'internal'
                        ? `/courses/${course.code}`
                        : `/courses/external/${course.code}`
                    }
                  >
                    {course.title}
                  </Link>
                </Button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
